import { TFunction } from 'i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import CalendarToday from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { SxProps, useTheme, Theme } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { DateRange } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ButtonResetIcon from '../ButtonResetIcon';
import {
  arrowStyling,
  dateFilterBtnRangeIconSx,
  dateFilterBtnResetSx,
  dateFilterButtonBadgeSx,
  dateFilterButtonDarkSx,
  dateFilterButtonLightSx,
  defaultButtonStyling,
  defaultOutlineButtonStyling,
  dateFilterBtnCalendarSx,
  selectedButtonStyling,
} from '../styles';
import { ButtonSize } from '../types';

interface DateFilterButtonProps {
  anchorEl: HTMLInputElement | HTMLButtonElement | null;
  buttonSize: ButtonSize;
  buttonText: string;
  dateButtonStyle?: SxProps<Theme> | undefined;
  handleReset: () => void;
  resetEnabled: boolean;
  predefined: boolean | undefined;
  handleButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  dates?: DateRange<dayjs.Dayjs>;
  date?: dayjs.Dayjs | null;
  iconOnly?: boolean;
  showBadge?: boolean;
  isComparison?: boolean;
}

export const dateText = (
  t: TFunction,
  date: dayjs.Dayjs | null | undefined,
  dates: DateRange<dayjs.Dayjs> | undefined,
  isComparison: boolean,
  inputText: string
) => {
  if (date?.isValid()) {
    return `${date.format('L')}`;
  }

  if (dates && dates[0]?.isValid() && dates[1]?.isValid()) {
    const isSameDay = dates[0].isSame(dates[1], 'day');
    const formattedRange = isSameDay ? dates[0].format('L') : `${dates[0].format('L')} - ${dates[1].format('L')}`;

    return isComparison ? `${t('sales.dateRanges.comparedTo')} ${formattedRange}` : formattedRange;
  }

  return inputText;
};

function DateFilterButton(props: DateFilterButtonProps) {
  const {
    anchorEl,
    buttonSize,
    buttonText,
    dateButtonStyle,
    handleReset,
    resetEnabled,
    predefined,
    handleButtonClick,
    dates,
    date,
    iconOnly,
    showBadge,
    isComparison = false,
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleButtonSx = () => {
    if (isDarkMode) {
      if (resetEnabled || predefined) {
        return dateFilterButtonDarkSx(theme);
      }
      return dateFilterButtonLightSx(theme);
    }

    if (iconOnly) {
      if (anchorEl || date?.isValid()) {
        return selectedButtonStyling(null, theme);
      }
      return defaultOutlineButtonStyling(theme);
    }

    if (resetEnabled || predefined) {
      return selectedButtonStyling(anchorEl, theme);
    }

    return defaultButtonStyling(anchorEl, theme);
  };

  const getEndIcon = () => {
    if (dateButtonStyle) {
      return <CalendarToday sx={dateFilterBtnCalendarSx(theme)} />;
    }
    if (anchorEl) {
      return <ArrowDropUpOutlinedIcon sx={arrowStyling(theme, Boolean(predefined))} fontSize='inherit' />;
    }
    if (!dateButtonStyle) {
      return <ArrowDropDownIcon sx={arrowStyling(theme, Boolean(predefined))} fontSize='inherit' />;
    }
    return null;
  };

  if (iconOnly) {
    return (
      <Button
        startIcon={
          <Badge
            color='error'
            variant='dot'
            invisible={!showBadge}
            slotProps={{
              badge: {
                'data-testid': 'date-picker-badge',
              } as object,
            }}
            sx={dateFilterButtonBadgeSx}
          >
            <CalendarToday color='primary' />
          </Badge>
        }
        role='button'
        aria-haspopup='true'
        data-testid='min-single-date-filter-button'
        sx={handleButtonSx}
        onClick={handleButtonClick}
      >
        <Typography variant='button' color={theme.palette.primary.main} pl={date?.isValid() ? '12px' : '0px'}>
          {date?.isValid() ? `${date.format('L')}` : ''}
        </Typography>
      </Button>
    );
  }

  return resetEnabled ? (
    <Button
      startIcon={predefined && !dateButtonStyle && <DateRangeIcon sx={dateFilterBtnRangeIconSx(theme)} />}
      role='button'
      aria-haspopup='true'
      data-testid={date ? 'single-date-filter-button' : 'date-filter-button'}
      sx={dateButtonStyle ?? handleButtonSx}
      onClick={handleButtonClick}
      size={buttonSize}
    >
      <Typography sx={dateFilterBtnResetSx(predefined, dateButtonStyle)} fontSize='inherit'>
        {dateText(t, date, dates, isComparison, buttonText)}
      </Typography>
      <ButtonResetIcon handleReset={handleReset} />
    </Button>
  ) : (
    <Button
      role='button'
      aria-haspopup='true'
      data-testid={date ? 'single-date-filter-button' : 'date-filter-button'}
      sx={dateButtonStyle ?? handleButtonSx}
      onClick={handleButtonClick}
      size={buttonSize}
      startIcon={predefined && !dateButtonStyle && <DateRangeIcon sx={dateFilterBtnRangeIconSx(theme)} />}
    >
      <Typography
        sx={{
          marginRight: '4px',
          marginLeft: `${predefined ? '4px' : '8px'}`,
          fontWeight: 500,
          lineHeight: '22px',
        }}
        fontSize='inherit'
      >
        {dateText(t, date, dates, isComparison, buttonText)}
      </Typography>
      {getEndIcon()}
    </Button>
  );
}

DateFilterButton.defaultProps = {
  dates: undefined,
  date: null,
  dateButtonStyle: null,
  iconOnly: false,
  showBadge: false,
  isComparison: false,
};

export default DateFilterButton;
